/**
 * WalletvenTerms.jsx — Términos y Condiciones / Terms & Conditions (ES/EN)
 * -------------------------------------------------------------------------
 * DEV NOTES (Muhammad):
 * 1. Fill in the COMPANY object below with Walletven's real legal details.
 *    Everything in [square brackets] is a placeholder to replace.
 * 2. This is a template written to the structure a regulated crypto on-ramp
 *    needs. It is NOT legal advice — have the client's lawyer review it before
 *    going live, especially the KYC/AML, risk and governing-law sections.
 * 3. CryptoPocket (Fintech Payments PSC, S.L.) is referenced as the external
 *    settlement/payment provider, which is accurate for the integration.
 * 4. Rebrand via the CSS variables at the top of `styles`.
 */

import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";

const COMPANY = {
  brandName: "Walletven",
  legalName: "Walletven — JMS Intermedia SLU",
 jurisdiction: "Madrid",
  address: "Garcilaso de la Vega 21, Local 12, 38005, Santa Cruz de Tenerife, Islas Canarias, España.",
  contactEmail: "hola@walletven.com",
  privacyEmail: "hola@walletven.com",
  website: "walletven.com",
//   effectiveDate: "[dd/mm/aaaa]",
  effectiveDate: "[01/04/2026]",

};

const fill = (s) =>
  s
    .replaceAll("%ENTITY%", COMPANY.legalName)
    .replaceAll("%BRAND%", COMPANY.brandName)
    .replaceAll("%JURIS%", COMPANY.jurisdiction)
    .replaceAll("%ADDRESS%", COMPANY.address)
    .replaceAll("%EMAIL%", COMPANY.contactEmail)
    .replaceAll("%PRIVEMAIL%", COMPANY.privacyEmail)
    .replaceAll("%SITE%", COMPANY.website)
    .replaceAll("%DATE%", COMPANY.effectiveDate);

const UI = {
  es: {
    docLabel: "Documento legal",
    title: "Términos y Condiciones",
    updated: "Última actualización",
    toc: "Contenido",
    top: "Volver arriba",
    langAria: "Cambiar idioma",
  },
  en: {
    docLabel: "Legal document",
    title: "Terms and Conditions",
    updated: "Last updated",
    toc: "Contents",
    top: "Back to top",
    langAria: "Change language",
  },
};

const SECTIONS = [
  {
    id: "intro",
    es: {
      title: "Introducción y aceptación",
      body: [
        "Estos Términos y Condiciones (los «Términos») regulan el acceso y uso de la plataforma %BRAND% y de los servicios relacionados ofrecidos por %ENTITY% («%BRAND%», «nosotros» o «la Plataforma») a través de %SITE%. Al registrarse, acceder o utilizar la Plataforma, usted acepta quedar vinculado por estos Términos en su totalidad.",
        "Si no está de acuerdo con alguna parte de estos Términos, debe abstenerse de utilizar la Plataforma. Le recomendamos leer detenidamente este documento y conservar una copia para su referencia.",
      ],
    },
    en: {
      title: "Introduction and acceptance",
      body: [
        "These Terms and Conditions (the \u201CTerms\u201D) govern access to and use of the %BRAND% platform and the related services provided by %ENTITY% (\u201C%BRAND%\u201D, \u201Cwe\u201D, or \u201Cthe Platform\u201D) through %SITE%. By registering for, accessing, or using the Platform, you agree to be bound by these Terms in full.",
        "If you do not agree with any part of these Terms, you must not use the Platform. We encourage you to read this document carefully and keep a copy for your records.",
      ],
    },
  },
  {
    id: "definitions",
    es: {
      title: "Definiciones",
      body: [
        "«Criptoactivo» significa una representación digital de valor o de derechos que puede transferirse y almacenarse electrónicamente mediante tecnología de registro distribuido, incluidas las monedas estables (stablecoins).",
        "«Usuario» significa cualquier persona física o jurídica que se registre o utilice la Plataforma. «Cuenta» significa el registro personal creado por el Usuario para acceder a los servicios.",
        "«Orden» significa una instrucción del Usuario para comprar, vender o convertir criptoactivos o fondos fiduciarios a través de la Plataforma.",
      ],
    },
    en: {
      title: "Definitions",
      body: [
        "\u201CCrypto-asset\u201D means a digital representation of value or rights that can be transferred and stored electronically using distributed ledger technology, including stablecoins.",
        "\u201CUser\u201D means any natural or legal person who registers for or uses the Platform. \u201CAccount\u201D means the personal record created by the User to access the services.",
        "\u201COrder\u201D means an instruction from the User to buy, sell, or convert crypto-assets or fiat funds through the Platform.",
      ],
    },
  },
  {
    id: "eligibility",
    es: {
      title: "Elegibilidad",
      body: [
        "Para utilizar la Plataforma, usted debe ser mayor de edad en su jurisdicción de residencia y tener plena capacidad legal para celebrar contratos vinculantes.",
        "La Plataforma no está disponible para personas residentes en territorios sujetos a sanciones o en jurisdicciones donde la prestación de los servicios sea ilegal. Usted declara que no figura en ninguna lista de sanciones internacionales ni actúa en nombre de una persona sancionada.",
        "Nos reservamos el derecho de restringir el acceso a la Plataforma en determinadas jurisdicciones a nuestra entera discreción.",
      ],
    },
    en: {
      title: "Eligibility",
      body: [
        "To use the Platform you must be of legal age in your jurisdiction of residence and have full legal capacity to enter into binding contracts.",
        "The Platform is not available to persons resident in sanctioned territories or in jurisdictions where providing the services would be unlawful. You represent that you are not on any international sanctions list and are not acting on behalf of a sanctioned person.",
        "We reserve the right to restrict access to the Platform in certain jurisdictions at our sole discretion.",
      ],
    },
  },
  {
    id: "account",
    es: {
      title: "Registro de cuenta y verificación (KYC/AML)",
      body: [
        "Para acceder a determinados servicios deberá crear una Cuenta y proporcionar información veraz, completa y actualizada. Usted es responsable de mantener la confidencialidad de sus credenciales y de toda actividad realizada en su Cuenta.",
        "De conformidad con la normativa aplicable en materia de prevención del blanqueo de capitales y de la financiación del terrorismo (PBC/FT), %BRAND% y sus proveedores están obligados a verificar su identidad («KYC»). Usted se compromete a facilitar los documentos e información que le solicitemos y autoriza su verificación.",
        "Podemos rechazar, suspender o limitar una Cuenta si la verificación no puede completarse, si detectamos información inexacta o si lo exige la normativa aplicable.",
      ],
    },
    en: {
      title: "Account registration and verification (KYC/AML)",
      body: [
        "To access certain services you must create an Account and provide accurate, complete, and up-to-date information. You are responsible for keeping your credentials confidential and for all activity carried out under your Account.",
        "In accordance with applicable anti-money-laundering and counter-terrorist-financing (AML/CTF) rules, %BRAND% and its providers are required to verify your identity (\u201CKYC\u201D). You agree to supply the documents and information we request and authorise their verification.",
        "We may refuse, suspend, or limit an Account if verification cannot be completed, if we detect inaccurate information, or where required by applicable law.",
      ],
    },
  },
  {
    id: "services",
    es: {
      title: "Descripción de los servicios",
      body: [
        "%BRAND% ofrece una infraestructura que permite a los Usuarios convertir fondos fiduciarios en criptoactivos y viceversa («on-ramp» y «off-ramp»), así como servicios de liquidación asociados. Los servicios concretos disponibles pueden variar según su jurisdicción y su nivel de verificación.",
        "La Plataforma actúa como interfaz tecnológica. La ejecución, custodia y liquidación de las operaciones pueden realizarse a través de proveedores externos regulados, según se describe en la sección de proveedores externos.",
        "No prestamos asesoramiento financiero, fiscal ni de inversión. Cualquier decisión relativa a criptoactivos es de su exclusiva responsabilidad.",
      ],
    },
    en: {
      title: "Description of services",
      body: [
        "%BRAND% provides infrastructure that lets Users convert fiat funds into crypto-assets and vice versa (\u201Con-ramp\u201D and \u201Coff-ramp\u201D), together with associated settlement services. The specific services available may vary depending on your jurisdiction and verification level.",
        "The Platform acts as a technology interface. Execution, custody, and settlement of transactions may be carried out through regulated external providers, as described in the third-party providers section.",
        "We do not provide financial, tax, or investment advice. Any decision relating to crypto-assets is your sole responsibility.",
      ],
    },
  },
  {
    id: "providers",
    es: {
      title: "Proveedores externos",
      body: [
        "Para procesar pagos, liquidar operaciones y facilitar el intercambio entre fondos fiduciarios y criptoactivos, %BRAND% se integra con proveedores externos regulados, entre ellos CryptoPocket (operado por Fintech Payments PSC, S.L.), autorizado bajo el reglamento europeo MiCA.",
        "El uso de determinados servicios puede estar sujeto a los términos y políticas de dichos proveedores, además de los presentes Términos. Al utilizar la Plataforma, usted autoriza la transmisión de la información necesaria a estos proveedores para la prestación del servicio.",
        "%BRAND% no controla ni se hace responsable de la disponibilidad, actuaciones u omisiones de los proveedores externos más allá de lo exigido por la ley aplicable.",
      ],
    },
    en: {
      title: "Third-party providers",
      body: [
        "To process payments, settle transactions, and facilitate the exchange between fiat funds and crypto-assets, %BRAND% integrates with regulated external providers, including CryptoPocket (operated by Fintech Payments PSC, S.L.), authorised under the European MiCA regulation.",
        "Use of certain services may be subject to those providers\u2019 own terms and policies in addition to these Terms. By using the Platform, you authorise the transmission of the information necessary to these providers to deliver the service.",
        "%BRAND% does not control and is not responsible for the availability, acts, or omissions of external providers beyond what applicable law requires.",
      ],
    },
  },
  {
    id: "fees",
    es: {
      title: "Comisiones y precios",
      body: [
        "El uso de la Plataforma puede estar sujeto a comisiones, márgenes de cambio y cargos de red, que se le mostrarán antes de confirmar una Orden. Al confirmar una Orden, usted acepta las comisiones aplicables.",
        "Los tipos de cambio de los criptoactivos son variables y se determinan en el momento de la ejecución. Podemos actualizar nuestra estructura de comisiones publicando la versión vigente en la Plataforma.",
      ],
    },
    en: {
      title: "Fees and pricing",
      body: [
        "Use of the Platform may be subject to fees, exchange spreads, and network charges, which will be shown to you before you confirm an Order. By confirming an Order, you accept the applicable fees.",
        "Crypto-asset exchange rates are variable and are determined at the time of execution. We may update our fee structure by publishing the current version on the Platform.",
      ],
    },
  },
  {
    id: "transactions",
    es: {
      title: "Órdenes, ejecución y reembolsos",
      body: [
        "Las Órdenes se ejecutan según las condiciones vigentes en el momento de su confirmación. Debido a la naturaleza de la tecnología de registro distribuido, las operaciones con criptoactivos son, por lo general, irreversibles una vez ejecutadas.",
        "Usted es responsable de proporcionar datos de destino correctos, incluidas las direcciones de las carteras. No seremos responsables de pérdidas derivadas de datos incorrectos facilitados por usted.",
        "Los reembolsos, cuando procedan, se regirán por la normativa aplicable y por las políticas de los proveedores de liquidación. Determinadas operaciones pueden no ser reembolsables una vez ejecutadas.",
      ],
    },
    en: {
      title: "Orders, execution, and refunds",
      body: [
        "Orders are executed according to the conditions in force at the time they are confirmed. Because of the nature of distributed ledger technology, crypto-asset transactions are generally irreversible once executed.",
        "You are responsible for providing correct destination details, including wallet addresses. We are not responsible for losses arising from incorrect details you provide.",
        "Refunds, where applicable, are governed by applicable law and by the settlement providers\u2019 policies. Certain transactions may be non-refundable once executed.",
      ],
    },
  },
  {
    id: "risks",
    es: {
      title: "Riesgos de los criptoactivos",
      body: [
        "Los criptoactivos son productos de alto riesgo. Su valor puede fluctuar de forma significativa y usted podría perder la totalidad del importe invertido. Los rendimientos pasados no garantizan resultados futuros.",
        "Los criptoactivos no suelen estar cubiertos por sistemas de garantía de depósitos ni por esquemas de protección al inversor. Usted asume plenamente los riesgos asociados a su uso.",
        "El entorno regulatorio de los criptoactivos está en evolución y los cambios normativos podrían afectar a la disponibilidad o a las condiciones de los servicios.",
      ],
    },
    en: {
      title: "Crypto-asset risks",
      body: [
        "Crypto-assets are high-risk products. Their value can fluctuate significantly and you could lose the entire amount invested. Past performance does not guarantee future results.",
        "Crypto-assets are generally not covered by deposit-guarantee schemes or investor-protection schemes. You fully assume the risks associated with their use.",
        "The regulatory environment for crypto-assets is evolving, and regulatory changes could affect the availability or conditions of the services.",
      ],
    },
  },
  {
    id: "prohibited",
    es: {
      title: "Usos prohibidos",
      body: [
        "Usted se compromete a no utilizar la Plataforma para actividades ilícitas, incluidos el blanqueo de capitales, la financiación del terrorismo, el fraude, la evasión de sanciones o cualquier otra conducta prohibida por la ley.",
        "Queda prohibido acceder a la Plataforma mediante medios automatizados no autorizados, intentar vulnerar su seguridad, suplantar identidades o facilitar información falsa durante la verificación.",
      ],
    },
    en: {
      title: "Prohibited use",
      body: [
        "You agree not to use the Platform for unlawful activities, including money laundering, terrorist financing, fraud, sanctions evasion, or any other conduct prohibited by law.",
        "You may not access the Platform through unauthorised automated means, attempt to breach its security, impersonate others, or provide false information during verification.",
      ],
    },
  },
  {
    id: "ip",
    es: {
      title: "Propiedad intelectual",
      body: [
        "Todos los derechos de propiedad intelectual sobre la Plataforma, incluidos el software, la marca «%BRAND%», los logotipos, los textos y el diseño, pertenecen a %ENTITY% o a sus licenciantes. Se le concede una licencia limitada, no exclusiva e intransferible para utilizar la Plataforma conforme a estos Términos.",
        "No podrá copiar, modificar, distribuir ni realizar ingeniería inversa de ningún componente de la Plataforma sin nuestra autorización previa por escrito.",
      ],
    },
    en: {
      title: "Intellectual property",
      body: [
        "All intellectual property rights in the Platform, including the software, the \u201C%BRAND%\u201D brand, logos, text, and design, belong to %ENTITY% or its licensors. You are granted a limited, non-exclusive, non-transferable licence to use the Platform in accordance with these Terms.",
        "You may not copy, modify, distribute, or reverse-engineer any component of the Platform without our prior written authorisation.",
      ],
    },
  },
  {
    id: "liability",
    es: {
      title: "Limitación de responsabilidad",
      body: [
        "En la máxima medida permitida por la ley, %ENTITY% no será responsable de daños indirectos, incidentales o consecuentes, ni de pérdida de beneficios, datos o fondo de comercio derivados del uso o la imposibilidad de uso de la Plataforma.",
        "Nada de lo dispuesto en estos Términos excluye o limita nuestra responsabilidad en los casos en que dicha exclusión o limitación no esté permitida por la ley aplicable.",
      ],
    },
    en: {
      title: "Limitation of liability",
      body: [
        "To the fullest extent permitted by law, %ENTITY% shall not be liable for indirect, incidental, or consequential damages, nor for loss of profits, data, or goodwill arising from the use of, or inability to use, the Platform.",
        "Nothing in these Terms excludes or limits our liability where such exclusion or limitation is not permitted by applicable law.",
      ],
    },
  },
  {
    id: "indemnity",
    es: {
      title: "Indemnización",
      body: [
        "Usted se compromete a mantener indemne a %ENTITY%, sus administradores, empleados y proveedores frente a cualquier reclamación, pérdida o gasto (incluidos honorarios legales razonables) derivados del incumplimiento de estos Términos o del uso indebido de la Plataforma.",
      ],
    },
    en: {
      title: "Indemnification",
      body: [
        "You agree to hold harmless %ENTITY%, its directors, employees, and providers against any claim, loss, or expense (including reasonable legal fees) arising from your breach of these Terms or misuse of the Platform.",
      ],
    },
  },
  {
    id: "suspension",
    es: {
      title: "Suspensión y terminación",
      body: [
        "Podemos suspender o cancelar su acceso a la Plataforma, con o sin previo aviso, si incumple estos Términos, si lo exige la normativa aplicable o si detectamos actividad sospechosa o riesgo para la seguridad.",
        "Usted puede cerrar su Cuenta en cualquier momento, sin perjuicio de las obligaciones legales de conservación de información que nos correspondan.",
      ],
    },
    en: {
      title: "Suspension and termination",
      body: [
        "We may suspend or terminate your access to the Platform, with or without notice, if you breach these Terms, where required by applicable law, or if we detect suspicious activity or a security risk.",
        "You may close your Account at any time, without prejudice to any legal record-keeping obligations that apply to us.",
      ],
    },
  },
  {
    id: "changes",
    es: {
      title: "Modificaciones",
      body: [
        "Podemos modificar estos Términos en cualquier momento. La versión vigente se publicará en la Plataforma con su fecha de entrada en vigor. El uso continuado de la Plataforma tras la publicación de cambios implica su aceptación.",
      ],
    },
    en: {
      title: "Changes to these terms",
      body: [
        "We may modify these Terms at any time. The current version will be published on the Platform together with its effective date. Continued use of the Platform after changes are published constitutes your acceptance of them.",
      ],
    },
  },
  {
    id: "law",
    es: {
      title: "Ley aplicable y resolución de disputas",
      body: [
        "Estos Términos se rigen por la legislación de %JURIS%. Cualquier controversia derivada de los mismos se someterá a los tribunales competentes de dicha jurisdicción, sin perjuicio de los derechos que le correspondan como consumidor conforme a la normativa imperativa aplicable.",
      ],
    },
    en: {
      title: "Governing law and dispute resolution",
      body: [
        "These Terms are governed by the laws of %JURIS%. Any dispute arising from them shall be submitted to the competent courts of that jurisdiction, without prejudice to any rights you may have as a consumer under applicable mandatory law.",
      ],
    },
  },
  {
    id: "contact",
    es: {
      title: "Contacto",
      body: [
        "Si tiene preguntas sobre estos Términos, puede contactarnos en %EMAIL% o en %ADDRESS%.",
      ],
    },
    en: {
      title: "Contact",
      body: [
        "If you have questions about these Terms, you can contact us at %EMAIL% or at %ADDRESS%.",
      ],
    },
  },
];

const styles = `
.wv-root{
  --wv-ink:#1a1a1f;
  --wv-bg:#ffffff;
  --wv-surface:#fff1f3;
  --wv-accent:#fc364e;
  --wv-accent-ink:#c8102e;
  --wv-border:#eae6e8;
  --wv-muted:#6b6b74;
  color:var(--wv-ink);
  background:var(--wv-bg);
  font-family:"Inter",ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  line-height:1.66;
  -webkit-font-smoothing:antialiased;
  scroll-behavior:smooth;
}
@media (prefers-reduced-motion: reduce){ .wv-root{scroll-behavior:auto;} }
.wv-root *{box-sizing:border-box;}
.wv-header{
  max-width:1220px;margin:0 auto;padding:56px 24px 28px;
}
.wv-eyebrow{
  display:inline-block;font-size:.72rem;letter-spacing:.14em;text-transform:uppercase;
  font-weight:700;color:var(--wv-accent-ink);background:var(--wv-surface);
  border:1px solid var(--wv-border);padding:5px 11px;border-radius:999px;
}
.wv-brand{margin:18px 0 0;font-size:.95rem;font-weight:700;color:var(--wv-muted);letter-spacing:.02em;}
.wv-title{margin:6px 0 0;font-size:clamp(1.9rem,4.5vw,2.9rem);font-weight:800;letter-spacing:-.02em;line-height:1.1;}
.wv-meta{margin:14px 0 0;font-size:.9rem;color:var(--wv-muted);}
.wv-langbar{display:flex;gap:6px;margin:22px 0 0;}
.wv-langbtn{
  font:inherit;font-size:.82rem;font-weight:700;cursor:pointer;
  padding:7px 16px;border-radius:8px;border:1px solid var(--wv-border);
  background:#fff;color:var(--wv-muted);transition:all .15s ease;
}
.wv-langbtn:hover{border-color:var(--wv-accent);color:var(--wv-accent-ink);}
.wv-langbtn[aria-pressed="true"]{background:var(--wv-accent);border-color:var(--wv-accent);color:#fff;}
.wv-langbtn:focus-visible{outline:2px solid var(--wv-accent-ink);outline-offset:2px;}
.wv-rule{max-width:1220px;margin:22px auto 0;padding:0 24px;}
.wv-rule span{display:block;height:1px;background:var(--wv-border);}
.wv-shell{
  max-width:1220px;margin:0 auto;padding:8px 24px 96px;
  display:grid;grid-template-columns:1fr;gap:40px;
}
@media (min-width:900px){
  .wv-shell{grid-template-columns:248px 1fr;gap:56px;align-items:start;}
}
.wv-toc{position:static;}
@media (min-width:900px){ .wv-toc{position:sticky;top:24px;} }
.wv-toc-title{font-size:.72rem;letter-spacing:.12em;text-transform:uppercase;font-weight:700;color:var(--wv-muted);margin:32px 0 12px;}
.wv-toc-list{list-style:none;margin:0;padding:0;border-left:2px solid var(--wv-border);}
.wv-toc-link{
  display:block;width:100%;text-align:left;font:inherit;font-size:.86rem;
  cursor:pointer;background:none;border:none;color:var(--wv-muted);
  padding:6px 0 6px 14px;margin-left:-2px;border-left:2px solid transparent;
  transition:color .15s ease,border-color .15s ease;
}
.wv-toc-link:hover{color:var(--wv-ink);}
.wv-toc-link[data-active="true"]{color:var(--wv-accent-ink);border-left-color:var(--wv-accent);font-weight:700;}
.wv-toc-link:focus-visible{outline:2px solid var(--wv-accent-ink);outline-offset:2px;border-radius:2px;}
.wv-content{min-width:0;padding-top:24px;}
.wv-section{scroll-margin-top:24px;padding:14px 0 26px;border-bottom:1px solid var(--wv-border);}
.wv-section:last-child{border-bottom:none;}
.wv-num{font-size:.78rem;font-weight:700;color:var(--wv-accent);letter-spacing:.04em;}
.wv-h2{margin:4px 0 12px;font-size:1.28rem;font-weight:750;letter-spacing:-.01em;}
.wv-p{margin:0 0 12px;max-width:70ch;color:#3b3b42;}
.wv-p:last-child{margin-bottom:0;}
.wv-top{
  display:inline-block;margin-top:26px;font-size:.82rem;font-weight:700;
  color:var(--wv-accent-ink);text-decoration:none;
}
.wv-top:hover{text-decoration:underline;}
`;

export default function WalletvenTerms() {
  const [lang, setLang] = useState("es");
  const [active, setActive] = useState(SECTIONS[0].id);
  const t = UI[lang];

  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [lang]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    setActive(id);
  };

  return (
    <div className="wv-root">
      <style>{styles}</style>

        <Navbar />


      <header className="wv-header">
        <span className="wv-eyebrow">{t.docLabel}</span>
        <p className="wv-brand">{COMPANY.brandName}</p>
        <h1 className="wv-title">{t.title}</h1>
        <p className="wv-meta">
          {t.updated}: {COMPANY.effectiveDate}
        </p>
        <div className="wv-langbar" role="group" aria-label={t.langAria}>
          <button
            className="wv-langbtn"
            aria-pressed={lang === "es"}
            onClick={() => setLang("es")}
          >
            Español
          </button>
          <button
            className="wv-langbtn"
            aria-pressed={lang === "en"}
            onClick={() => setLang("en")}
          >
            English
          </button>
        </div>
      </header>

      <div className="wv-rule">
        <span />
      </div>

      <div className="wv-shell">
        <nav className="wv-toc" aria-label={t.toc}>
          <p className="wv-toc-title">{t.toc}</p>
          <ul className="wv-toc-list">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <button
                  className="wv-toc-link"
                  data-active={active === s.id}
                  onClick={() => go(s.id)}
                >
                  {s[lang].title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="wv-content">
          {SECTIONS.map((s, i) => (
            <section key={s.id} id={s.id} className="wv-section">
              <span className="wv-num">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="wv-h2">{s[lang].title}</h2>
              {s[lang].body.map((para, j) => (
                <p key={j} className="wv-p">
                  {fill(para)}
                </p>
              ))}
            </section>
          ))}
          <a
            className="wv-top"
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              const reduce = window.matchMedia(
                "(prefers-reduced-motion: reduce)"
              ).matches;
              window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
            }}
          >
            ↑ {t.top}
          </a>
        </main>
      </div>
    </div>
  );
}